import { useRouter } from 'next/router';
import { format, formatDistanceStrict, isValid } from 'date-fns';

import { Box, Heading, Paragraph, Popover, Text, TextButton, TriggerText } from '@zillow/constellation-10';

import { LoadingButton, LoadingParagraph } from '@zg-rentals/loading-components';
import {
  useRentalManagerUserFreemiumWebApiHandler_isTopOfFunnelEnrollmentEligible,
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertySummaryWebApiHandler_listing,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import accessUtils from '../../../../utils/accessUtils';
import numberUtils from '../../../../utils/numberUtils';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../../constants/FreemiumConstants';
import { LISTING_SUMMARY_DISPLAY_STATUS } from '../../../../constants/ListingSummaryConstants';
import TimeConstants from '../../../../constants/TimeConstants';
import { getCreateListingRoute, getListingRoute } from '../../../../constants/urls';

import SimpleCard from '../../../../components/SimpleCard';

import { singleTextLineStyles } from '../../../../styles/text';
import { containerStyles, detailContainerStyles, verticalLineStyles } from '../../../ListingPage/styles';

import { useAuth } from '../../../AuthProvider';
import ConnectedModuleCard from '../../../ConnectedModuleCard';
import ListingAvailability from './ListingAvailability';
import { ListingModuleHeading } from './ListingModuleHeading';
import NetworkView, { DISPLAY_TYPE } from './NetworkView';
import PremiumCTA from './PremiumCTA';
import WhenListingExpireLearnMore from './WhenListingExpireLearnMore';

const rentZestimateLink = 'https://www.zillow.com/z/rental/what-is-a-rent-zestimate/' as const;
const activeForLink =
  'https://www.zillow.help/article/how-is-the-days-listed-counter-determined-zd30237392565907' as const;

type Props = {
  listingAlias: string;
};

const ListingModule = ({ listingAlias }: Props) => {
  const router = useRouter();
  const { user } = useAuth();

  const { data, isPending } = useRentalManagerUserPropertySummaryWebApiHandler_listing(
    { listingAlias },
    { enabled: Boolean(listingAlias), staleTime: TimeConstants.SECONDS_10 },
  );

  const { data: overviewData } = useRentalManagerUserPropertyDetailsWebApiHandler_overview(
    { listingAlias },
    { enabled: Boolean(listingAlias) },
  );

  const isActive = data?.isActive ?? false;
  const lastActivatedMs = data?.lastActivatedMs;
  const expirationMs = data?.expirationMs;
  const displayStatus = data?.syndication?.overview?.displayStatus?.toLowerCase() ?? '';
  const rentZestimate = data?.rentZestimate ?? 0;
  const isInactive = displayStatus === LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE;
  const isMultifamily = overviewData?.isMultifamily ?? false;
  const shouldCheckIn = overviewData?.listingAvailability?.shouldCheckIn ?? false;

  const freemium = overviewData?.freemium;
  const isFreemiumEnabled = freemium?.isEnabled ?? false;
  const activeProductType = freemium?.activeProduct?.type;
  const isActiveProductTypePremium = activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM;
  const isActiveProductTypeFree = activeProductType === PRODUCT_TYPE_NAME_TO_ID.FREE;
  const activeProductExpirationTimestamp = freemium?.activeProduct?.expirationTimestamp;

  const { data: isTopOfFunnelEnrollmentEligible = false } =
    useRentalManagerUserFreemiumWebApiHandler_isTopOfFunnelEnrollmentEligible({
      enabled: isFreemiumEnabled,
      meta: { tags: ['isTopOfFunnelEnrollmentEligible'], context: { api: 'freemium', details: 'ListingModule' } },
    });

  const displayPremiumCTA =
    accessUtils.canAccessTopOfFunnelPremium({
      isFreemiumEnabled,
      isActiveProductTypePremium,
      isTopOfFunnelEnrollmentEligible,
    }) && isActive;

  const getListingTypeHeading = () => {
    if (isFreemiumEnabled && isActiveProductTypePremium) return 'Premium listing';
    if (isFreemiumEnabled && isActive && isActiveProductTypeFree) return 'Basic listing';
    return 'Listing';
  };

  const lastListed =
    lastActivatedMs && isValid(new Date(lastActivatedMs)) ? format(new Date(lastActivatedMs), 'MMM yyyy') : '–';
  const listedFor =
    lastActivatedMs && displayStatus !== LISTING_SUMMARY_DISPLAY_STATUS.DECLINED
      ? formatDistanceStrict(new Date(), new Date(lastActivatedMs))
      : '-';

  const canAccessModularListing = accessUtils.canAccessModularListing({
    isMultifamily: overviewData?.isMultifamily ?? false,
    listingTypeCode: overviewData?.listingType?.code ?? 0,
    lastActivatedMs: lastActivatedMs,
    userCanAccessModularListing: user?.features?.canAccessModularListing ?? false,
  });

  const listingPath = canAccessModularListing
    ? `/rental-manager/properties/${listingAlias}/create-listing`
    : `/rental-manager/properties/${listingAlias}/listing`;
  const activeCTA = 'Edit your listing';
  const inactiveCTA = 'List your property';

  const renderActiveDetails = () => (
    <Box css={detailContainerStyles}>
      <LoadingParagraph
        textStyle="body-sm"
        isLoading={isPending}
        loadingWidth="40%"
        css={{ marginBottom: '100', whiteSpace: 'nowrap' }}
      >
        Active for{' '}
        <Popover
          header={<Heading level={6}>Calculating your active time</Heading>}
          body={
            <>
              <Paragraph css={{ marginBottom: '400' }}>
                This counter shows the amount of time since you last activated your listing. If you deactivate and then
                reactivate within 31 days, your public listing will show the cumulative time it has been active.
              </Paragraph>
              <Popover.Close>
                <TextButton asChild>
                  <a target="_blank" href={activeForLink} rel="noopener noreferrer">
                    Learn more
                  </a>
                </TextButton>
              </Popover.Close>
            </>
          }
        />
      </LoadingParagraph>
      <LoadingParagraph
        textStyle="body-bold"
        isLoading={isPending}
        loadingWidth="100%"
        css={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
      >
        {listedFor}
      </LoadingParagraph>
    </Box>
  );

  const renderInactiveDetails = () => (
    <>
      <Box css={detailContainerStyles}>
        <Text
          textStyle="body-sm"
          asChild
          css={{ marginBottom: '100', color: 'text.neutral', textOverflow: 'ellipsis', overflow: 'hidden' }}
        >
          <p>Last listed</p>
        </Text>
        <Text
          textStyle="body-bold"
          asChild
          css={{ color: 'text.neutral', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
        >
          <p>{lastListed}</p>
        </Text>
      </Box>
      {rentZestimate !== 0 && (
        <>
          <Box css={verticalLineStyles} data-testid="StyledVerticalLine" />
          <Box css={detailContainerStyles}>
            <Text
              textStyle="body-sm"
              asChild
              css={{
                marginBottom: '100',
                color: 'text.subtle',
                whiteSpace: { base: 'normal', md: 'nowrap' },
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }}
            >
              <p>Rent Zestimate®</p>
            </Text>
            <Text
              textStyle="body-bold"
              asChild
              css={{ color: 'text.subtle', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
            >
              <p>
                <Popover
                  header={<Heading level={6}>Rent Zestimate®</Heading>}
                  body={
                    <>
                      The Rent Zestimate is Zillow&apos;s best estimate of a home&apos;s value as a rental property.{' '}
                      <TextButton asChild textStyle="body">
                        <a target="_blank" href={rentZestimateLink} rel="noopener noreferrer">
                          Learn more
                        </a>
                      </TextButton>
                      {' about the Rent Zestimate.'}
                    </>
                  }
                  trigger={<TriggerText>{numberUtils.asCurrency(rentZestimate)}</TriggerText>}
                />
              </p>
            </Text>
          </Box>
        </>
      )}
    </>
  );

  const handleGoToListingClick = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    event.preventDefault();
    router.push(canAccessModularListing ? getCreateListingRoute({ listingAlias }) : getListingRoute({ listingAlias }));
  };

  const renderCTA = (
    <TextButton tone="brand" asChild>
      <a href={listingPath} target="_self">
        Details
      </a>
    </TextButton>
  );

  return (
    <ConnectedModuleCard
      data-testid="ListingModule"
      leftComponent={
        <ListingModuleHeading
          isPremiumListing={isFreemiumEnabled && isActiveProductTypePremium}
          headingText={getListingTypeHeading()}
        />
      }
      ctaComponent={!isPending && renderCTA}
      ctaFontType="body-sm"
      justifyHeadingContent={'space-between'}
      isLoading={false}
      includeBorderTop={isFreemiumEnabled && isActiveProductTypePremium}
    >
      {expirationMs && shouldCheckIn && (
        <Box css={{ marginBottom: '800' }}>
          <ListingAvailability listingAlias={listingAlias} expirationMs={expirationMs} />
        </Box>
      )}

      <SimpleCard>
        <Box css={{ ...containerStyles, marginBottom: '0' }} data-testid="Details-Simple">
          {isInactive ? renderInactiveDetails() : renderActiveDetails()}
        </Box>
      </SimpleCard>

      {isActive && !(expirationMs && shouldCheckIn) && (
        <WhenListingExpireLearnMore listingAlias={listingAlias} isLoading={isPending} />
      )}

      {displayPremiumCTA && isActiveProductTypePremium && (
        <PremiumCTA
          listingAlias={listingAlias}
          isLoading={isPending}
          isActiveProductTypeFree={isActiveProductTypeFree}
          activeProductExpirationTimestamp={activeProductExpirationTimestamp}
        />
      )}

      <Box css={{ marginBottom: { base: '400', lg: '600' } }} />

      {isActive ? (
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: {
              base: 'repeat(1, minmax(0, 1fr))',
              md: 'repeat(auto-fit, minmax(0, 1fr))',
            },
            gap: '200',
          }}
        >
          <Box>
            <NetworkView
              displayType={DISPLAY_TYPE.DROPDOWN_BUTTON}
              label="View your listing"
              size="md"
              status={data?.syndication?.overview?.status}
              syndication={data?.syndication}
              isMultifamily={isMultifamily}
              isLoading={isPending}
            />
          </Box>
          <Box>
            <LoadingButton
              tone="neutral"
              emphasis="outlined"
              fluid={true}
              onClick={handleGoToListingClick}
              title={activeCTA}
              isLoading={isPending}
              loadingWidth="100%"
              data-testid="ListingModule-OpenListingButton"
            >
              <Text textStyle="body-bold" css={singleTextLineStyles}>
                {activeCTA}
              </Text>
            </LoadingButton>
          </Box>
        </Box>
      ) : (
        <LoadingButton
          fluid={true}
          tone="brand"
          emphasis="outlined"
          onClick={handleGoToListingClick}
          title={inactiveCTA}
          isLoading={isPending}
          loadingWidth="100%"
        >
          <Text textStyle="body-bold" css={{ color: 'inherit', ...singleTextLineStyles }}>
            {inactiveCTA}
          </Text>
        </LoadingButton>
      )}
    </ConnectedModuleCard>
  );
};

export default ListingModule;
